import { useMutation, useQuery } from "@tanstack/react-query";
import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

import { queryClient } from "../../../App";
import { API } from "../apiService";
import { isUserAuthorizationExpired, isUserAuthorizationToken, stripAuthorizationPrefix } from "../auth/authToken";

export function useJobs({ params }) {
	return useQuery({
		queryKey: ["jobs", JSON.stringify(params)],
		queryFn: async () => await API.get("jobs", { params }),
		staleTime: 60 * 1000 * 60,
		retry: 0,
	});
}

export function useJob(jobId) {
	return useQuery({
		queryKey: ["jobs", jobId],
		queryFn: async () => await API.get(`jobs/${jobId}`),
		staleTime: 60 * 1000 * 60,
		enabled: !!jobId,
	});
}

export function prefetchJob(jobId) {
	if (!jobId) return;
	return queryClient.prefetchQuery({
		queryKey: ["jobs", jobId],
		queryFn: async () => await API.get(`jobs/${jobId}`),
		staleTime: 60 * 1000 * 60,
	});
}

export function prefetchJobsByCategory(category) {
	if (!category) return;
	const params = {
		title: "",
		country: "Tanzania",
		region: "",
		city: "",
		jobExpertise: category.toLowerCase() !== "all" ? [category] : [],
		pageNumber: 1,
	};
	return queryClient.prefetchQuery({
		queryKey: ["jobs", JSON.stringify(params)],
		queryFn: async () => await API.get("jobs", { params }),
		staleTime: 60 * 1000 * 60,
	});
}

export function useCompanies() {
	return useQuery({
		queryKey: ["companies"],
		queryFn: async () => await API.get("companies"),
		staleTime: Infinity,
	});
}

export function useCompany(id, options = {}) {
	return useQuery({
		queryKey: ["companies", id],
		queryFn: async () => await API.get(`companies?companyId=${id}`, { authMode: options.authMode }),
		staleTime: Infinity,
		enabled: !!id && (options.enabled ?? true),
	});
}

export function useCompanyJobs(id, options = {}) {
	return useQuery({
		queryKey: ["companies", id, "jobs"],
		queryFn: async () =>
			await (options.authMode === "claim"
				? API.get("job-market", { authMode: options.authMode })
				: API.get(`/jobs/company?companyId=${id}`, { authMode: options.authMode })),
		staleTime: 86400000,
		enabled: !!id && (options.enabled ?? true),
	});
}

export function useCompanyActivities(companyId, params = {}, options = {}) {
	return useQuery({
		queryKey: ["companies", companyId, "activities", params],
		queryFn: async () =>
			await API.get(`companies/${companyId}/activities`, {
				authMode: "claim",
				params,
			}),
		staleTime: 60 * 1000,
		enabled: !!companyId && (options.enabled ?? true),
	});
}

function invalidateCompanyDashboardQueries(companyId) {
	if (!companyId) return;
	queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
	queryClient.invalidateQueries({ queryKey: ["companies", companyId, "jobs"] });
	queryClient.invalidateQueries({ queryKey: ["companies", companyId, "activities"] });
}

export function useUpdateClaimCompanyMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data } = await API.put(`companies/${companyId}`, payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useCreateClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data } = await API.post("job-market", payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useUpdateClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id, payload }) => {
			const { data } = await API.put(`job-market/${id}`, payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useDeleteClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.delete(`job-market/${id}`, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function usePublishClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.patch(`job-market/${id}`, undefined, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useExtendClaimCompanyJobDeadlineMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id, deadlineDate }) => {
			const { data } = await API.patch(`job-market/${id}/extend-deadline`, { deadlineDate }, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useMarkClaimCompanyJobFilledMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.patch(`job-market/${id}/mark-filled`, undefined, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function prefetchCompany(companyId) {
	if (!companyId) return;
	return queryClient.prefetchQuery({
		queryKey: ["companies", companyId],
		queryFn: async () => await API.get(`companies?companyId=${companyId}`),
		staleTime: Infinity,
	});
}

export function prefetchCompanyJobs(companyId) {
	if (!companyId) return;
	return queryClient.prefetchQuery({
		queryKey: ["companies", companyId, "jobs"],
		queryFn: async () => await API.get(`/jobs/company?companyId=${companyId}`),
		staleTime: 86400000,
	});
}

export function useActivelyHiringCompanies() {
	return useQuery({
		queryKey: ["companies", "active"],
		queryFn: async () => await API.get("/jobs/employers-actively-hiring"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useJobCategories() {
	return useQuery({
		queryKey: ["jobs", "categories"],
		queryFn: async () => await API.get("jobs/categories"),
		staleTime: Infinity,
	});
}

export function useLatestJobs() {
	return useQuery({
		queryKey: ["jobs", "latest"],
		queryFn: async () => await API.get("jobs/latest"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useUpcomingJobs() {
	return useQuery({
		queryKey: ["jobs", "upcoming"],
		queryFn: async () => await API.get("jobs/upcoming"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useFeaturedEmployers() {
	return useQuery({
		queryKey: ["employers", "featured"],
		queryFn: async () => await API.get("jobs/employers/featured"),
		staleTime: 60 * 1000,
	});
}

const cookie = new Cookies();
function hasActiveUserAuthorization() {
	return isUserAuthorizationToken() && !isUserAuthorizationExpired();
}

export function useRecommendedJobs(areasOfExpertise = "") {
	return useQuery({
		queryKey: ["jobs", "recommended"],
		queryFn: async () => {
			const { data } = await API.get("jobs/recommended");
			return data;
		},
		staleTime: 86400 * 1000,
		enabled: hasActiveUserAuthorization() && !!areasOfExpertise,
		retry: 0,
	});
}

export function useSavedJobs() {
	return useQuery({
		queryKey: ["jobs", "saved"],
		queryFn: async () => {
			const { data } = await API.get("jobs/saved");
			return data;
		},
		retry: 0,
		staleTime: 60 * 1000 * 60,
		enabled: hasActiveUserAuthorization(),
	});
}

export function useDeleteJobMutation(id) {
	return useMutation({
		mutationFn: async () => await API.delete(`jobs/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries(["jobs", "saved", "recommended"]);
		},
	});
}

export function useUserAccount() {
	return useQuery({
		queryKey: ["user", "account"],
		queryFn: async () => {
			const { data } = await API.get("users/account");

			const baseUserData = {
				email: data.emailAddress,
				userID: data._id,
				lastName: data.lastName,
				firstName: data.firstName,
				picture: data.picture,
				isDropzOpsUser: data.isDropzOpsUser || false,
				jobPreferences: data.jobPreferences || {},
				job_preference_progress_status: data.job_preference_progress_status || {},
			};

			// LinkedIn users should use their own profile data
			const authToken = cookie.get("Authorization");

			if (authToken?.includes("LI::")) {
				return baseUserData;
			}

			// For other auth methods (Google, Cognito)
			const token = stripAuthorizationPrefix(authToken);
			if (data.isDropzOpsUser) {
				localStorage.setItem("isDropzOpsUser", true);
			}

			try {
				const decodedToken = decodeToken(token);
				const data = {
					...baseUserData,
					lastName: decodedToken?.family_name || baseUserData.lastName,
					firstName: decodedToken?.given_name || baseUserData.firstName,
					picture: decodedToken?.picture || baseUserData.picture,
				};
				return data;
			} catch (error) {
				console.log(error);
				return baseUserData;
			}
		},
		retry: 0,
		enabled: isUserAuthorizationToken(),
		staleTime: 60 * 30 * 1000,
	});
}

export function useResumes() {
	return useQuery({
		queryKey: ["resumes"],
		queryFn: async () => {
			const { data } = await API.get("resume-ledger");
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
	});
}

export function useResume(id) {
	return useQuery({
		queryKey: ["resume", id],
		queryFn: async () => {
			const { data } = await API.get(`resume-ledger/${id}`);
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
		enabled: !!id,
	});
}

export function useSmartProfile() {
	return useQuery({
		queryKey: ["smartProfile"],
		queryFn: async () => {
			const { data } = await API.get("smart-profiles");
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
		enabled: !!cookie.get("Authorization"),
	});
}

export function useDSPInfo(dspId) {
	return useQuery({
		queryKey: ["dspInfo", dspId],
		queryFn: async () => {
			const { data } = await API.get("smart-profiles", {
				params: { dspId: dspId },
			});
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
		enabled: !!dspId,
	});
}

export function useCreateSmartProfile(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data: response } = await API.post("smart-profiles", payload);
			return response;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile"]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useUpdateSmartProfile(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const _data = { ...payload };
			const dspId = payload.dspId;
			delete _data.dspId;

			const { data: response } = await API.patch(`smart-profiles/${dspId}`, _data);
			return response;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile", ["dspInfo"]]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useDeleteDocument(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const params = {
				docId: payload.doc_id,
				docType: payload.doc_type,
				dspId: payload.dspId,
			};
			return await API.delete(`smart-profiles/docs/${payload.doc_id}`, {
				params,
			});
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile", "dspInfo"]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useApplications() {
	return useQuery({
		queryKey: ["applications"],
		queryFn: async () => {
			const { data } = await API.get("job-applications");
			return data;
		},
		staleTime: 60 * 1000 * 60,
		retry: 0,
	});
}

export function useUpdateApplicationStatus({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ applicationId, payload }) => {
			const { data: response } = await API.patch(`job-applications/application/${applicationId}`, payload);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}

export function useUpdateApplicationStage({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ applicationId, stageId, payload }) => {
			const { data: response } = await API.patch(
				`job-applications/application/${applicationId}/stage/${stageId}`,
				payload,
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}

export function useCreateApplication({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ payload }) => {
			const { data: response } = await API.post("job-applications", payload);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}
